import React, { useCallback, useState } from 'react';
import { FlashOn } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Modal from './general/modal';
import { ReduxStore } from '../constants/types';
import { Bill, PaymentMethod } from '../pages/hospital/Billing/types';
import { getBillByUuidService, quickPayBillService } from '../services/billing';
import { InsuranceClaimsApiService } from '../pages/hospital/InsuranceClaims/services/insuranceClaimsApi';
import { evaluateQuickPayEligibility } from '../utils/quickPayEligibility';
import { formatCurrency } from '../utils/index.jsx';

interface QuickPayButtonProps extends LocalizeContextProps {
    uuidInvestigation: string;
    billUuid: string;
    currency: string;
    languageCode: string;
    onSuccess: () => void;
    size?: 'small' | 'medium' | 'large';
    /** When false, success is only reported via onSuccess (no inline caption). */
    showInlineSuccess?: boolean;
}

const QuickPayButton: React.FC<QuickPayButtonProps> = ({
    uuidInvestigation,
    billUuid,
    currency,
    languageCode,
    onSuccess,
    size = 'medium',
    showInlineSuccess = true,
    translate,
}) => {
    const paymentMethods: PaymentMethod[] = useSelector(
        (state: ReduxStore) => state.investigations.currentInvestigation?.billingInfo?.paymentMethods ?? [],
    );
    const cashPaymentMethodId = paymentMethods.find((method) => method.code === 'CASH')?.id ?? null;

    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [bill, setBill] = useState<Bill | null>(null);
    const [patientAmountDue, setPatientAmountDue] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [paying, setPaying] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadEligibility = useCallback(async (): Promise<boolean> => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const billResponse = await getBillByUuidService(uuidInvestigation, billUuid);
            const loadedBill = billResponse.bill;
            let externalCoverage = 0;
            let claimsLoaded = true;
            try {
                const claims = await new InsuranceClaimsApiService().getClaimsForBill(
                    uuidInvestigation,
                    billUuid,
                );
                externalCoverage = claims.reduce((sum, claim) => sum + (claim.requestedAmount ?? 0), 0);
            } catch {
                claimsLoaded = false;
            }

            const eligibility = evaluateQuickPayEligibility(loadedBill, {
                externalCoverageAmount: externalCoverage,
                cashPaymentMethodId,
                claimsLoaded,
            });
            if (!eligibility.eligible) {
                setErrorMessage(eligibility.reason ?? 'not_eligible');
                return false;
            }

            setBill(loadedBill);
            setPatientAmountDue(eligibility.patientAmountDue ?? 0);
            return true;
        } catch {
            setErrorMessage('load_failed');
            return false;
        } finally {
            setLoading(false);
        }
    }, [uuidInvestigation, billUuid, cashPaymentMethodId]);

    const handleOpen = async () => {
        const ok = await loadEligibility();
        if (ok) {
            setShowConfirm(true);
        }
    };

    const handleConfirm = async () => {
        if (!bill) return;
        if (patientAmountDue > 0 && !cashPaymentMethodId) {
            setErrorMessage('no_cash_method');
            return;
        }
        setPaying(true);
        setErrorMessage(null);
        try {
            await quickPayBillService(
                uuidInvestigation,
                billUuid,
                patientAmountDue,
                cashPaymentMethodId ?? undefined,
            );
            setShowConfirm(false);
            if (showInlineSuccess) {
                setSuccessMessage(
                    translate('pages.hospital.outpatients.quick_pay.success', {
                        amount: formatCurrency(patientAmountDue, currency, languageCode),
                    }).toString(),
                );
            }
            onSuccess();
        } catch {
            setErrorMessage('pay_failed');
        } finally {
            setPaying(false);
        }
    };

    return (
        <>
            <Tooltip title={<Translate id="pages.hospital.outpatients.quick_pay.tooltip" />}>
                <span>
                    <IconButton
                        data-testid="quick-pay-button"
                        aria-label="quick-pay"
                        size={size}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleOpen();
                        }}
                        disabled={loading || paying}
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <FlashOn style={{ color: '#fbc02d' }} fontSize={size} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            {errorMessage && !showConfirm && (
                <Typography variant="caption" color="error" component="span" ml={0.5}>
                    <Translate id={`pages.hospital.outpatients.quick_pay.error.${errorMessage}`} />
                </Typography>
            )}

            {showInlineSuccess && successMessage && (
                <Typography variant="caption" color="primary" component="span" ml={0.5}>
                    {successMessage}
                </Typography>
            )}

            <Modal
                open={showConfirm}
                title={<Translate id="pages.hospital.outpatients.quick_pay.confirm_title" />}
                closeModal={() => setShowConfirm(false)}
                confirmAction={handleConfirm}
                disabled={paying}
                confirmButtonLabel="general.confirm"
            >
                <>
                    <Typography variant="body1" gutterBottom>
                        <Translate
                            id="pages.hospital.outpatients.quick_pay.confirm_message"
                            data={{ amount: formatCurrency(patientAmountDue, currency, languageCode) }}
                        />
                    </Typography>
                    {errorMessage === 'pay_failed' && (
                        <Typography variant="body2" color="error">
                            <Translate id="pages.hospital.outpatients.quick_pay.error.pay_failed" />
                        </Typography>
                    )}
                </>
            </Modal>
        </>
    );
};

export default withLocalize(QuickPayButton);
